import { useEffect, useRef, useState } from 'react';
import { useStyletron } from 'baseui';
import { useTranslation } from 'react-i18next';

import ENGTCheckBox from 'components/UI/ENGTCheckBox/ENGTCheckBox';
import ModalAlert from 'components/UI/Modal/ModalAlert/ModalAlert';

import { IObjProps } from 'shared/consts/types';
import { isValidResponseObject } from 'shared/helpers';

import { API as CampaignAPI } from 'store/Campaign/api';

import { AGENT_APIS } from 'pages/Agents/api';

import { RETARGET_OUTCOME_TRANSLATION_KEYS, RETARGET_OUTCOMES, RetargetOutcome } from '../constants';

interface IRetargetVoiceCampaignModal {
	onClose: () => void;
	onContinue: (selection: { outcomes: RetargetOutcome[]; dispositions: string[] }) => void;
	sourceAgentId?: string;
	sourceCampaignId: string;
}

const RetargetVoiceCampaignModal = ({
	onClose,
	onContinue,
	sourceAgentId,
	sourceCampaignId,
}: IRetargetVoiceCampaignModal) => {
	const [css, theme]: [any, any] = useStyletron();
	const { t } = useTranslation(['common', 'pages']);
	const [selectedOutcomes, setSelectedOutcomes] = useState<RetargetOutcome[]>([]);
	const [dispositions, setDispositions] = useState<string[]>([]);
	const [selectedDispositions, setSelectedDispositions] = useState<string[]>([]);
	const dispositionAgentIdRef = useRef<string | null>(null);

	useEffect(() => {
		const getDispositions = (agentId?: string) => {
			if (!agentId) {
				dispositionAgentIdRef.current = null;
				setDispositions([]);

				return;
			}
			if (dispositionAgentIdRef.current === agentId) {
				return;
			}
			dispositionAgentIdRef.current = agentId;
			AGENT_APIS.getAgentDetails(agentId).then((response: IObjProps) => {
				if (!isValidResponseObject(response) || dispositionAgentIdRef.current !== agentId) {
					return;
				}
				setDispositions(
					[...(response.data.responseObject?.evaluate?.dispositions || [])]
						.filter((disposition) => typeof disposition === 'string' && disposition.trim())
						.map((disposition) => disposition.trim())
				);
			});
		};

		if (sourceAgentId) {
			getDispositions(sourceAgentId);

			return;
		}
		CampaignAPI.getVoiceCampaignDetails(sourceCampaignId).then((response: IObjProps) => {
			getDispositions(isValidResponseObject(response) ? response.data.responseObject?.agentId : undefined);
		});
	}, [sourceAgentId, sourceCampaignId]);

	const toggleOutcome = (outcome: RetargetOutcome, isChecked: boolean) => {
		setSelectedOutcomes((current) =>
			isChecked ? [...current, outcome] : current.filter((selected) => selected !== outcome)
		);
	};
	const toggleDisposition = (disposition: string, isChecked: boolean) =>
		setSelectedDispositions((current) =>
			isChecked ? [...current, disposition] : current.filter((selected) => selected !== disposition)
		);
	const isEngagedSelected = selectedOutcomes.includes('ENGAGED');

	const labels: Record<RetargetOutcome, string> = {
		ENGAGED: t(RETARGET_OUTCOME_TRANSLATION_KEYS.ENGAGED),
		NOT_REACHABLE: t(RETARGET_OUTCOME_TRANSLATION_KEYS.NOT_REACHABLE),
		FAILED: t(RETARGET_OUTCOME_TRANSLATION_KEYS.FAILED),
	};

	return (
		<ModalAlert
			closeBtnLabel={t('common:cancel')}
			confirmBtnLabel={t('common:continue')}
			description={t('pages:voiceCampaign.retarget.description')}
			heading={t('pages:voiceCampaign.retarget.heading')}
			isConfirmBtnDisabled={
				selectedOutcomes.length === 0 ||
				(isEngagedSelected && dispositions.length > 0 && !selectedDispositions.length)
			}
			isOpen
			onClose={onClose}
			onConfirm={() => onContinue({ outcomes: selectedOutcomes, dispositions: selectedDispositions })}
		>
			<div
				className={css({
					display: 'flex',
					flexDirection: 'column',
					gap: '1rem',
					marginTop: '1rem',
				})}
			>
				{RETARGET_OUTCOMES.map((outcome) => (
					<ENGTCheckBox
						key={outcome}
						labelCss={{
							color: theme.modalTextColor,
							lineHeight: '1.25rem',
						}}
						values={{
							isChecked: selectedOutcomes.includes(outcome),
							label: labels[outcome],
						}}
						onChange={(isChecked) => toggleOutcome(outcome, isChecked)}
					/>
				))}
				{isEngagedSelected && dispositions.length > 0 && (
					<div>
						<div className={css({ color: theme.modalTextColor, fontWeight: 600, marginBottom: '0.5rem' })}>
							{t('pages:voiceCampaign.retarget.dispositionsLabel')}
						</div>
						{dispositions.map((disposition) => (
							<ENGTCheckBox
								key={disposition}
								values={{ isChecked: selectedDispositions.includes(disposition), label: disposition }}
								onChange={(isChecked) => toggleDisposition(disposition, isChecked)}
							/>
						))}
					</div>
				)}
			</div>
		</ModalAlert>
	);
};

export default RetargetVoiceCampaignModal;
